import React, { useEffect, useState } from 'react'
import { components } from 'react-select'
import Utils from 'common/utils/utils'
import Constants from 'common/constants'
import { GroupLabel } from 'components/base/select/SearchableSelect'
import SearchableSelect, {
  OptionType,
} from 'components/base/select/SearchableSelect'

interface RuleConditionPropertySelectProps {
  ruleIndex: number
  propertyValue: string
  dataTest: string
  setRuleProperty: (
    index: number,
    property: string,
    value: { value: string | boolean },
  ) => void
  operator: string
  allowedContextValues: OptionType[]
  isValueFromContext: boolean
}

const RuleConditionPropertySelect = ({
  allowedContextValues,
  dataTest,
  isValueFromContext,
  operator,
  propertyValue,
  ruleIndex,
  setRuleProperty,
}: RuleConditionPropertySelectProps) => {
  const [localCurrentValue, setLocalCurrentValue] = useState(propertyValue)

  useEffect(() => {
    setLocalCurrentValue(propertyValue)
  }, [propertyValue])

  // Filter invalid context values from flagsmith and format them as options
  const contextOptions = allowedContextValues?.map(
    (contextValue: OptionType) => ({
      label: contextValue?.label,
      value: contextValue?.value,
    }),
  )

  const displayedLabel =
    contextOptions.find((option) => option.value === propertyValue)?.label ||
    propertyValue
  const isEditing = localCurrentValue !== propertyValue
  const showTraitOptions =
    localCurrentValue && (!isValueFromContext || isEditing)
  const traitAsGroupedOptions = showTraitOptions
    ? [
        {
          label: (
            <GroupLabel
              groupName='Traits'
              tooltipText={Constants.strings.USER_PROPERTY_DESCRIPTION}
            />
          ),
          options: [{ label: localCurrentValue, value: localCurrentValue }],
        },
      ]
    : []

  const contextAsGroupedOptions =
    contextOptions?.length > 0
      ? [
          {
            label: (
              <GroupLabel
                groupName='Context'
                tooltipText={
                  operator === 'PERCENTAGE_SPLIT'
                    ? Constants.strings.PERCENTAGE_SPLIT_DEFAULT_TO_IDENTITY_KEY
                    : undefined
                }
              />
            ),
            options: contextOptions,
          },
        ]
      : []

  const optionsWithTrait = [
    ...traitAsGroupedOptions,
    ...contextAsGroupedOptions,
  ]

  const showTitle = !showTraitOptions && operator !== 'PERCENTAGE_SPLIT'

  return (
    <>
      <SearchableSelect
        dataTest={dataTest}
        value={propertyValue}
        isClearable={true}
        placeholder={'Trait / Context value'}
        options={optionsWithTrait}
        noOptionsMessage={'Start typing to select a trait'}
        onBlur={() => {
          setRuleProperty(ruleIndex, 'property', { value: localCurrentValue })
        }}
        onInputChange={(e: string, metadata: any) => {
          if (metadata.action !== 'input-change') {
            return
          }
          setLocalCurrentValue(e)
        }}
        onChange={(e: { value: string; label: string }) => {
          setRuleProperty(ruleIndex, 'property', {
            value: Utils.safeParseEventValue(e?.value),
          })
        }}
        displayedLabel={displayedLabel}
        components={{
          Menu: ({ ...props }: any) => {
            return (
              <components.Menu {...props}>
                <React.Fragment>
                  {showTitle && (
                    <p
                      style={{ fontStyle: 'italic', paddingTop: 6 }}
                      className='mb-0 faint text-center'
                    >
                      Pick a value or type a trait name
                    </p>
                  )}
                  {props.children}
                </React.Fragment>
              </components.Menu>
            )
          },
        }}
      />
    </>
  )
}

export default RuleConditionPropertySelect
